import { useState, useEffect, useCallback, useRef } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { startConnect, fetchConfigCheck } from "../services/socialOAuth";
import { trackEvent } from "../lib/trackEvent";
import RedirectUriHelp from "../components/social/RedirectUriHelp";
import SocialChannelCard from "../components/social/SocialChannelCard";
import BlueskyConnectModal from "../components/social/BlueskyConnectModal";
import ChannelSetupAssistant from "../components/social/ChannelSetupAssistant";
import FacebookPageSelector from "../components/social/FacebookPageSelector";
import { recoverMessage } from "../components/social/recoverMessage";
import { TIKTOK_VIDEO_ENABLED } from "../config/features";
import { useLanguage } from "../lib/i18n/LanguageContext";
import {
  Wifi, WifiOff, RefreshCw, ExternalLink,
  CheckCircle, AlertTriangle, XCircle, Loader2,
  Facebook, Instagram, Linkedin, Activity, Zap,
  Info, Copy, CopyCheck, ChevronRight, ArrowRight, ListChecks,
  Youtube,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lokale Entwicklungsumgebung erkennen (kein REACT_APP_BACKEND_URL oder localhost gesetzt)
const IS_LOCAL_DEV =
  !process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_BACKEND_URL.includes("localhost");

// ─── PlatformStatus-Konstanten (spiegeln Backend-Enum) ───────────────────────
// Niemals Rohstrings vergleichen — immer S.XYZ nutzen.
// Änderungen hier müssen mit PlatformStatus in backend/social_connect/models.py abgeglichen werden.
const S = {
  READY:        "ready_to_post",
  CONNECTED:    "connected",
  NOT_CONNECTED:"not_connected",
  RECONNECT:    "reconnect_needed",
  EXPIRED:      "token_expired",
  INCOMPLETE:   "incomplete_permissions",
  ERROR:        "error",
  CONNECTING:   "connecting",
};

// Statuses, die "Handlungsbedarf" in der SummaryBar zählen
const WARN_STATUSES = [S.RECONNECT, S.EXPIRED, S.INCOMPLETE, S.ERROR];

// ─── Platform-Konfiguration ──────────────────────────────────────────────────

// Inline-Brand-Icons (kein lucide-Pendant) — konsistent zum TikTok/Google-Muster
const ThreadsIcon = ({ size, className }) => (
  <svg width={size} height={size} className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.5 2C7 2 4 5.5 4 12s3 10 8.5 10c3.7 0 6-1.6 7.2-4l-2.1-1c-.8 1.6-2.2 2.8-5.1 2.8-3.9 0-5.4-2.7-5.5-6.6 0-3.9 1.6-6.6 5.5-6.6 2.6 0 4 1 4.8 2.6.5 1 .6 2 .6 2.3 0 1.6-.9 2.7-2.6 2.7-1 0-1.8-.5-1.8-1.4 0-.8.6-1.3 1.6-1.3.3 0 .6 0 .8.1 0-1-.7-1.6-1.7-1.6-1.4 0-2.4 1.1-2.4 2.8 0 1.9 1.4 3.1 3.5 3.1 2.8 0 4.6-1.9 4.6-4.8C20.2 5.4 17.2 2 12.5 2z" />
  </svg>
);
const PinterestIcon = ({ size, className }) => (
  <svg width={size} height={size} className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.5 2 4 5.6 4 8.9c0 2 .8 3.8 2.4 4.4.3.1.5 0 .6-.3l.2-.9c.1-.3 0-.4-.2-.6-.5-.6-.8-1.3-.8-2.4 0-3 2.3-5.7 5.9-5.7 3.2 0 5 2 5 4.6 0 3.5-1.5 6.4-3.8 6.4-1.3 0-2.2-1-1.9-2.3.3-1.5 1-3 1-4.1 0-.9-.5-1.7-1.6-1.7-1.2 0-2.2 1.3-2.2 3 0 1.1.4 1.8.4 1.8l-1.5 6.3c-.3 1.4-.1 3.1 0 3.5 0 .1.2.2.3.1.1-.2 1.5-1.9 2-3.7l.7-2.7c.4.8 1.5 1.4 2.7 1.4 3.6 0 6-3.3 6-7.7C20.5 5.1 17.6 2 12 2z" />
  </svg>
);
const BlueskyIcon = ({ size, className }) => (
  <svg width={size} height={size} className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 10.8C10.7 8.3 7.2 4 4.7 4 3.4 4 2 4.9 2 7.3c0 2.4 1.4 5 2.5 5.6.8.5 1.7.6 2.5.5-1.5.3-2.9 1-1.5 3.1 1.1 1.6 3.3.4 4.5-1.4.6-.9 1-1.9 1.5-2.9.5 1 .9 2 1.4 2.9 1.2 1.8 3.4 3 4.5 1.4 1.4-2.1 0-2.8-1.5-3.1.8.1 1.7 0 2.5-.5C20.6 12.3 22 9.7 22 7.3 22 4.9 20.6 4 19.3 4c-2.5 0-6 4.3-7.3 6.8z" />
  </svg>
);

const PLATFORMS = [
  {
    id: "facebook",
    label: "Facebook",
    description: "Facebook-Seite verwalten & Posts veröffentlichen",
    descriptionEn: "Manage your Facebook Page & publish posts",
    icon: Facebook,
    color: "bg-blue-600",
    textColor: "text-blue-600",
    borderColor: "border-blue-200",
    bgLight: "bg-blue-50",
    oauthFlow: true,
  },
  {
    id: "instagram",
    label: "Instagram",
    description: "Instagram Business-Konto — läuft über Facebook OAuth",
    descriptionEn: "Instagram Business account — connected via Facebook OAuth",
    icon: Instagram,
    color: "bg-gradient-to-br from-purple-500 to-pink-500",
    textColor: "text-pink-600",
    borderColor: "border-pink-200",
    bgLight: "bg-pink-50",
    linkedTo: "facebook",
    oauthFlow: false,
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    description: "LinkedIn-Profil & Unternehmensposts",
    descriptionEn: "LinkedIn profile & company posts",
    icon: Linkedin,
    color: "bg-sky-700",
    textColor: "text-sky-700",
    borderColor: "border-sky-200",
    bgLight: "bg-sky-50",
    oauthFlow: true,
    useWizard: true,   // geführter Schritt-für-Schritt-Flow
  },
  {
    id: "tiktok",
    label: "TikTok",
    description: TIKTOK_VIDEO_ENABLED
      ? "TikTok Creator-Konto für Video-Posts"
      : "TikTok-Konto verbinden (Video-Posting in Prüfung)",
    descriptionEn: TIKTOK_VIDEO_ENABLED
      ? "TikTok Creator account for video posts"
      : "Connect your TikTok account (video posting under review)",
    icon: ({ size, className }) => (
      <svg width={size} height={size} className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V9.19a8.16 8.16 0 004.77 1.52V7.26a4.85 4.85 0 01-1-.57z" />
      </svg>
    ),
    color: "bg-gray-900",
    textColor: "text-gray-900",
    borderColor: "border-gray-300",
    bgLight: "bg-gray-50",
    oauthFlow: true,
    useWizard: true,   // geführter Schritt-für-Schritt-Flow (HTTPS-Tunnel-Hinweis inklusive)
  },
  {
    id: "google_business",
    label: "Google Business",
    description: "Google Unternehmensprofil Beiträge",
    descriptionEn: "Google Business Profile posts",
    icon: ({ size, className }) => (
      <svg width={size} height={size} className={className} viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
    ),
    color: "bg-white border border-gray-200",
    textColor: "text-gray-700",
    borderColor: "border-orange-200",
    bgLight: "bg-orange-50",
    oauthFlow: true,
  },
  {
    id: "threads",
    label: "Threads",
    description: "Threads-Konto (Meta) für Text-Beiträge",
    descriptionEn: "Threads account (Meta) for text posts",
    icon: ThreadsIcon,
    color: "bg-black",
    textColor: "text-stone-900",
    borderColor: "border-stone-300",
    bgLight: "bg-stone-50",
    oauthFlow: true,
  },
  {
    id: "pinterest",
    label: "Pinterest",
    description: "Pinterest-Pins aus deinen Beiträgen",
    descriptionEn: "Pinterest Pins from your posts",
    icon: PinterestIcon,
    color: "bg-red-600",
    textColor: "text-red-600",
    borderColor: "border-red-200",
    bgLight: "bg-red-50",
    oauthFlow: true,   // echter OAuth-Flow (Backend angebunden)
  },
  {
    id: "bluesky",
    label: "Bluesky",
    description: "Bluesky-Konto per Handle + App-Passwort",
    descriptionEn: "Bluesky account via handle + app password",
    icon: BlueskyIcon,
    color: "bg-sky-500",
    textColor: "text-sky-500",
    borderColor: "border-sky-200",
    bgLight: "bg-sky-50",
    oauthFlow: true,   // Verbindung per App-Passwort-Modal (kein Redirect)
  },
  {
    id: "youtube",
    label: "YouTube",
    description: "YouTube-Kanal für Shorts (Video-Posts)",
    descriptionEn: "YouTube channel for Shorts (video posts)",
    icon: Youtube,
    color: "bg-red-600",
    textColor: "text-red-600",
    borderColor: "border-red-200",
    bgLight: "bg-red-50",
    oauthFlow: true,   // echter OAuth-Flow (Backend angebunden)
  },
];

// Hilfsfunktion: Plattform-ID → lesbarer Name
const PLATFORM_LABELS = {
  facebook: "Facebook",
  instagram: "Instagram",
  linkedin: "LinkedIn",
  tiktok: "TikTok",
  google_business: "Google Business",
  threads: "Threads",
  pinterest: "Pinterest",
  bluesky: "Bluesky",
  youtube: "YouTube",
};
const platformLabel = (id) => PLATFORM_LABELS[id] || id;

// ─── ConnectWizardPanel ──────────────────────────────────────────────────────
//
// Geführter 4-Schritt-Flow für LinkedIn und TikTok.
// Wird als Inline-Panel über den Platform-Cards angezeigt, wenn der User
// auf "Schritt für Schritt verbinden" klickt.
//
// Wizard-State wird in sessionStorage persistiert, damit er nach dem
// OAuth-Redirect (Plattform → App) wiederhergestellt werden kann.
//
// Steps:
//   1 — Voraussetzungen prüfen (Config-Ampel, Redirect-URI sichtbar)
//   2 — Redirect-URI ins Developer-Portal eintragen (Copy-Button, Portal-Link)
//   3 — OAuth-Flow starten (Warnung bei TikTok lokal)
//   4 — Ergebnis (Erfolg oder Fehler nach Callback-Rückkehr)

const WIZARD_SESSION_KEY = "ff_connect_wizard";

const WIZARD_CFG = {
  linkedin: {
    label: "LinkedIn",
    color: "bg-sky-700",
    lightBg: "bg-sky-50",
    border: "border-sky-200",
    textColor: "text-sky-700",
    portalName: "LinkedIn Developer Portal",
    Icon: Linkedin,
  },
  tiktok: {
    label: "TikTok",
    color: "bg-gray-900",
    lightBg: "bg-gray-50",
    border: "border-gray-200",
    textColor: "text-gray-800",
    portalName: "TikTok Developer Portal",
    Icon: ({ size, className }) => (
      <svg width={size} height={size} className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V9.19a8.16 8.16 0 004.77 1.52V7.26a4.85 4.85 0 01-1-.57z" />
      </svg>
    ),
  },
};

// Kleines Step-Indicator-Widget
const StepDots = ({ current, total = 4 }) => (
  <div className="flex items-center gap-1.5">
    {Array.from({ length: total }, (_, i) => (
      <div
        key={i}
        className={`rounded-full transition-all ${
          i + 1 === current
            ? "w-5 h-2 bg-fox-fire"
            : i + 1 < current
            ? "w-2 h-2 bg-green-400"
            : "w-2 h-2 bg-stone-200"
        }`}
      />
    ))}
  </div>
);

// Ampel-Zeile für einzelne Voraussetzung
const CheckRow = ({ ok, label, hint }) => (
  <div className="flex items-start gap-2 py-1.5">
    {ok ? (
      <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
    ) : (
      <XCircle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
    )}
    <div>
      <span className={`text-xs font-medium ${ok ? "text-stone-700" : "text-red-600"}`}>
        {label}
      </span>
      {hint && <p className="text-[11px] text-stone-400 mt-0.5">{hint}</p>}
    </div>
  </div>
);

const ConnectWizardPanel = ({
  platformId,       // "linkedin" | "tiktok"
  setupData,        // data from /api/social-oauth/setup-status
  onStartConnect,   // (platformId) => void — startet den OAuth-Flow
  onClose,          // () => void
  initialStep,      // 1–4 (aus sessionStorage nach Redirect)
  callbackResult,   // null | "success" | "error"
  callbackMessage,  // Fehlertext aus URL-Params
}) => {
  const [step, setStep] = useState(initialStep || 1);
  const [copied, setCopied] = useState(false);
  const panelRef = useRef(null);
  const { t } = useLanguage();

  const cfg = WIZARD_CFG[platformId];
  const data = setupData?.[platformId];
  const PlatIcon = cfg?.Icon;

  const redirectUri = data?.redirect_uri || "";
  const portalReady = data?.portal_ready || false;
  const isTikTokLocal = platformId === "tiktok" && data?.is_local;
  const isConnected = data?.connected;
  const connectedName = data?.connected_name;
  const warnings = data?.warnings || [];

  // Beim ersten Render auf Panel scrollen
  useEffect(() => {
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, []);

  // Wenn initialStep=4 gesetzt wird (nach Redirect), einmal "aufwachen"
  useEffect(() => {
    if (initialStep >= 2) setStep(initialStep);
  }, [initialStep]);

  const handleCopy = () => {
    if (!redirectUri) return;
    navigator.clipboard.writeText(redirectUri).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleStartOAuth = () => {
    // Wizard-State in sessionStorage sichern — überlebt den OAuth-Redirect
    sessionStorage.setItem(
      WIZARD_SESSION_KEY,
      JSON.stringify({ platform: platformId, step: 3 })
    );
    onStartConnect(platformId);
  };

  if (!cfg) return null;
  // data may be null when backend is offline — render wizard with empty defaults

  return (
    <div
      ref={panelRef}
      className={`rounded-2xl border-2 ${cfg.border} ${cfg.lightBg} p-5 space-y-4 animate-fade-in`}
    >
      {/* ── Wizard-Header ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${cfg.color}`}>
            <PlatIcon size={18} className="text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-stone-800 text-sm">
              {t(`${cfg.label} verbinden`, `Connect ${cfg.label}`)}
            </h3>
            <p className={`text-[11px] ${cfg.textColor} font-medium`}>
              {t(`Schritt ${step} von 4`, `Step ${step} of 4`)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <StepDots current={step} />
          <button
            onClick={onClose}
            className="text-stone-400 hover:text-stone-600 transition-colors p-1"
            title={t("Wizard schließen", "Close wizard")}
          >
            <XCircle size={18} />
          </button>
        </div>
      </div>

      {/* ════════════════ SCHRITT 1: Voraussetzungen ════════════════ */}
      {step === 1 && (
        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-semibold text-stone-800 flex items-center gap-1.5 mb-1">
              <ListChecks size={14} className="text-stone-500" />
              {t("Voraussetzungen prüfen", "Check prerequisites")}
            </h4>
            <p className="text-xs text-stone-500">
              {t(
                "Bevor du die Verbindung starten kannst, müssen diese Punkte erfüllt sein.",
                "Before you can start the connection, these points need to be fulfilled.",
              )}
            </p>
          </div>

          {/* Backend nicht erreichbar */}
          {!data && (
            <div className="flex items-center gap-2 p-2.5 bg-stone-50 border border-stone-200 rounded-xl">
              <Loader2 size={13} className="text-stone-400 animate-spin flex-shrink-0" />
              <p className="text-xs text-stone-500">
                {t(
                  "Backend nicht erreichbar — Konfiguration kann nicht geprüft werden. Starte das Backend und lade die Seite neu.",
                  "Backend not reachable — the configuration can't be checked. Start the backend and reload the page.",
                )}
              </p>
            </div>
          )}

          <div className="bg-white rounded-xl border border-stone-100 p-3 space-y-0.5">
            <CheckRow
              ok={!!data?.client_id_masked}
              label={platformId === "linkedin" ? t("LINKEDIN_CLIENT_ID gesetzt", "LINKEDIN_CLIENT_ID set") : t("TIKTOK_CLIENT_KEY gesetzt", "TIKTOK_CLIENT_KEY set")}
              hint={!data?.client_id_masked ? t("Fehlende Client-ID in .env hinterlegen", "Add the missing client ID to .env") : t(`Aktuell: ${data.client_id_masked}`, `Current: ${data.client_id_masked}`)}
            />
            <CheckRow
              ok={!!data?.client_secret_set}
              label={t("Client Secret gesetzt", "Client secret set")}
              hint={!data?.client_secret_set ? t("Client Secret in .env hinterlegen", "Add the client secret to .env") : null}
            />
            <CheckRow
              ok={!!redirectUri}
              label={t("Redirect-URI konfiguriert", "Redirect URI configured")}
              hint={!redirectUri ? t(`${platformId === "linkedin" ? "LINKEDIN" : "TIKTOK"}_REDIRECT_URI in .env setzen`, `Set ${platformId === "linkedin" ? "LINKEDIN" : "TIKTOK"}_REDIRECT_URI in .env`) : redirectUri}
            />
            {platformId === "tiktok" && (
              <CheckRow
                ok={!isTikTokLocal}
                label={t("Öffentliche HTTPS-URI", "Public HTTPS URI")}
                hint={
                  isTikTokLocal
                    ? t(
                        "TikTok Web-Login braucht eine öffentliche https-URL. Lokal ngrok nutzen oder Demo-Modus verwenden.",
                        "TikTok web login requires a public https URL. Use ngrok locally, or use demo mode.",
                      )
                    : null
                }
              />
            )}
          </div>

          {/* Warnings */}
          {warnings.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-1">
              {warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-2">
                  <AlertTriangle size={12} className="text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-700">{w}</p>
                </div>
              ))}
            </div>
          )}

          {/* TikTok-Demo-Hinweis */}
          {isTikTokLocal && (
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-3">
              <p className="text-xs text-sky-700 font-medium mb-1">{t("Demo-Modus verfügbar", "Demo mode available")}</p>
              <p className="text-xs text-sky-600">
                {t(
                  "Da du lokal entwickelst, kannst du den TikTok-Wizard trotzdem durchlaufen — der OAuth-Flow wird simuliert. Für echtes Posting musst du später eine öffentliche URI + App-Review einrichten.",
                  "Since you're developing locally, you can still go through the TikTok wizard — the OAuth flow is simulated. For real posting, you'll need to set up a public URI + app review later.",
                )}
              </p>
            </div>
          )}

          <div className="flex items-center justify-between pt-1">
            <span className={`text-xs font-medium ${portalReady || isTikTokLocal ? "text-green-600" : "text-amber-600"}`}>
              {portalReady
                ? t("Konfiguration vollständig", "Configuration complete")
                : isTikTokLocal
                ? t("Demo-Modus möglich", "Demo mode possible")
                : t("Konfiguration unvollständig", "Configuration incomplete")}
            </span>
            <button
              onClick={() => setStep(2)}
              disabled={!portalReady && !isTikTokLocal}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-fox-fire text-white hover:bg-orange-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t("Weiter", "Next")} <ChevronRight size={12} />
            </button>
          </div>
        </div>
      )}

      {/* ════════════════ SCHRITT 2: Redirect-URI eintragen ════════════════ */}
      {step === 2 && (
        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-semibold text-stone-800 mb-1">
              {t("Redirect-URI ins Developer-Portal eintragen", "Enter the redirect URI in the developer portal")}
            </h4>
            <p className="text-xs text-stone-500">
              {t(
                "Kopiere diese URL und trage sie exakt so im Developer-Portal ein. Selbst ein fehlendes ",
                "Copy this URL and enter it exactly like this in the developer portal. Even a missing ",
              )}
              <code className="bg-stone-100 px-1 rounded">/</code>
              {t(" am Ende kann den Login verhindern.", " at the end can prevent login.")}
            </p>
          </div>

          {/* Redirect-URI Box */}
          <div className="bg-white border border-stone-200 rounded-xl p-3">
            <p className="text-[10px] text-stone-400 uppercase font-semibold tracking-wide mb-1.5">
              {t("Redirect-URI (exakt kopieren)", "Redirect URI (copy exactly)")}
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs text-stone-700 bg-stone-50 rounded-lg px-3 py-2 font-mono break-all leading-relaxed">
                {redirectUri || t("— nicht gesetzt —", "— not set —")}
              </code>
              <button
                onClick={handleCopy}
                disabled={!redirectUri}
                className={`flex-shrink-0 flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  copied
                    ? "bg-green-100 text-green-700"
                    : "bg-stone-100 hover:bg-stone-200 text-stone-600"
                }`}
                title={t("In Zwischenablage kopieren", "Copy to clipboard")}
              >
                {copied ? <CopyCheck size={13} /> : <Copy size={13} />}
                {copied ? t("Kopiert!", "Copied!") : t("Kopieren", "Copy")}
              </button>
            </div>
          </div>

          {/* Portal-Anweisung */}
          <div className="bg-white border border-stone-200 rounded-xl p-3 space-y-2">
            <p className="text-[10px] text-stone-400 uppercase font-semibold tracking-wide">
              {t(`Was im ${cfg.portalName} tun?`, `What to do in the ${cfg.portalName}`)}
            </p>
            <p className="text-xs text-stone-600 leading-relaxed">
              {data?.portal_action || "—"}
            </p>
            {/* LinkedIn: Required Products */}
            {platformId === "linkedin" && data?.required_products && (
              <div className="mt-1">
                <p className="text-[11px] text-stone-500 font-medium mb-1">
                  {t('Auch aktivieren (Tab „Products"):', 'Also enable (tab "Products"):')}
                </p>
                <ul className="space-y-0.5">
                  {data.required_products.map((p) => (
                    <li key={p} className="flex items-center gap-1.5 text-xs text-stone-600">
                      <div className="w-1 h-1 rounded-full bg-sky-400 flex-shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Portal-Link */}
          {data?.portal_url && (
            <a
              href={data.portal_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 w-full px-4 py-2.5 rounded-xl border border-stone-200 text-xs font-medium text-stone-600 hover:bg-stone-50 transition-colors"
            >
              <ExternalLink size={13} />
              {t(`${cfg.portalName} öffnen`, `Open ${cfg.portalName}`)}
            </a>
          )}

          <div className="flex items-center justify-between pt-1">
            <button
              onClick={() => setStep(1)}
              className="text-xs text-stone-400 hover:text-stone-600 transition-colors"
            >
              {t("← Zurück", "← Back")}
            </button>
            <button
              onClick={() => setStep(3)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-fox-fire text-white hover:bg-orange-600 transition-colors"
            >
              {t("URI eingetragen — weiter", "URI entered — next")} <ChevronRight size={12} />
            </button>
          </div>
        </div>
      )}

      {/* ════════════════ SCHRITT 3: Verbinden ════════════════ */}
      {step === 3 && (
        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-semibold text-stone-800 mb-1">
              {t("Jetzt verbinden", "Connect now")}
            </h4>
            <p className="text-xs text-stone-500">
              {t(
                `Du wirst zur offiziellen ${cfg.label}-Anmeldeseite weitergeleitet. Melde dich an und erteile alle Berechtigungen.`,
                `You will be redirected to the official ${cfg.label} login page. Log in and grant all permissions.`,
              )}
            </p>
          </div>

          {/* TikTok-lokale Warnung */}
          {isTikTokLocal && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <AlertTriangle size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-amber-800 mb-0.5">
                  {t("Lokale Entwicklung — TikTok Demo-Modus", "Local development — TikTok demo mode")}
                </p>
                <p className="text-xs text-amber-700">
                  {t(
                    "Echter Web-Login erfordert eine öffentliche https-URI. Im Demo-Modus wird der OAuth-Flow simuliert — für echtes Posting später ngrok oder einen Staging-Server nutzen.",
                    "Real web login requires a public https URI. In demo mode, the OAuth flow is simulated — use ngrok or a staging server later for real posting.",
                  )}
                </p>
              </div>
            </div>
          )}

          {/* LinkedIn: kurze Erinnerung an Products */}
          {platformId === "linkedin" && !isTikTokLocal && (
            <div className="flex items-start gap-2 p-3 bg-sky-50 border border-sky-200 rounded-xl">
              <Info size={13} className="text-sky-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-sky-700">
                {t(
                  "Stelle sicher, dass im LinkedIn Developer Portal beide Produkte aktiviert sind:",
                  "Make sure both products are enabled in the LinkedIn Developer Portal:",
                )}{" "}
                <strong>Share on LinkedIn</strong> &amp;{" "}
                <strong>Sign In with LinkedIn</strong>.
              </p>
            </div>
          )}

          {/* Checkliste was passiert */}
          <div className="bg-white border border-stone-200 rounded-xl p-3 space-y-1.5">
            <p className="text-[10px] text-stone-400 uppercase font-semibold tracking-wide mb-2">
              {t("Was jetzt passiert", "What happens now")}
            </p>
            {[
              t("Du wirst zur Anmeldeseite weitergeleitet", "You'll be redirected to the login page"),
              t("Melde dich mit deinem Konto an", "Log in with your account"),
              t("Bestätige alle angeforderten Berechtigungen", "Confirm all requested permissions"),
              t("Du kehrst automatisch hierher zurück", "You'll automatically return here"),
            ].map((line, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-stone-600">
                <div className="w-4 h-4 rounded-full bg-stone-100 text-stone-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                  {i + 1}
                </div>
                {line}
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between pt-1">
            <button
              onClick={() => setStep(2)}
              className="text-xs text-stone-400 hover:text-stone-600 transition-colors"
            >
              {t("← Zurück", "← Back")}
            </button>
            <button
              onClick={handleStartOAuth}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors shadow-sm ${cfg.color} hover:opacity-90`}
            >
              <ArrowRight size={14} />
              {t(`${cfg.label} verbinden`, `Connect ${cfg.label}`)}
            </button>
          </div>
        </div>
      )}

      {/* ════════════════ SCHRITT 4: Ergebnis ════════════════ */}
      {step === 4 && (
        <div className="space-y-3">
          {callbackResult === "success" || isConnected ? (
            <>
              <div className="flex flex-col items-center gap-3 py-4">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle size={28} className="text-green-500" />
                </div>
                <div className="text-center">
                  <h4 className="text-base font-semibold text-stone-800">
                    {t(`${cfg.label} erfolgreich verbunden!`, `${cfg.label} connected successfully!`)}
                  </h4>
                  {connectedName && (
                    <p className="text-sm text-stone-500 mt-0.5">
                      {t("Konto:", "Account:")} <span className="font-medium text-stone-700">{connectedName}</span>
                    </p>
                  )}
                </div>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-xs text-green-700 text-center">
                {t(
                  `Posts auf ${cfg.label} können jetzt automatisch veröffentlicht werden.`,
                  `Posts on ${cfg.label} can now be published automatically.`,
                )}
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 rounded-xl text-xs font-semibold border border-stone-200 text-stone-600 hover:bg-stone-50 transition-colors"
                >
                  {t("Schließen", "Close")}
                </button>
                {/* Nächste nicht verbundene Plattform vorschlagen */}
                {platformId === "linkedin" && (
                  <button
                    onClick={() => onClose("tiktok")}
                    className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-semibold bg-gray-900 text-white hover:bg-black transition-colors"
                  >
                    {t("TikTok verbinden", "Connect TikTok")} <ChevronRight size={12} />
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col items-center gap-3 py-4">
                <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle size={28} className="text-red-400" />
                </div>
                <div className="text-center">
                  <h4 className="text-base font-semibold text-stone-800">
                    {t("Verbindung fehlgeschlagen", "Connection failed")}
                  </h4>
                  {callbackMessage && (
                    <p className="text-xs text-stone-500 mt-1 max-w-xs leading-relaxed">
                      {callbackMessage}
                    </p>
                  )}
                </div>
              </div>

              <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 space-y-1.5">
                <p className="text-[10px] text-stone-400 uppercase font-semibold tracking-wide">
                  {t("Häufige Ursachen", "Common causes")}
                </p>
                {platformId === "linkedin" ? (
                  <>
                    <p className="text-xs text-stone-600">{t("• Redirect-URI im Portal nicht eingetragen oder falsch", "• Redirect URI not entered in the portal, or incorrect")}</p>
                    <p className="text-xs text-stone-600">{t('• „Share on LinkedIn" Produkt nicht aktiviert', '• "Share on LinkedIn" product not enabled')}</p>
                    <p className="text-xs text-stone-600">{t("• App-Eigentümer-Login im Dev-Modus nötig", "• App owner login required in dev mode")}</p>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-stone-600">{t("• Redirect-URI nicht eingetragen oder nicht https", "• Redirect URI not entered, or not https")}</p>
                    <p className="text-xs text-stone-600">{t("• App-Review für video.publish noch ausstehend", "• App review for video.publish still pending")}</p>
                    <p className="text-xs text-stone-600">{t("• Lokal: Demo-Modus statt echtem OAuth nutzen", "• Locally: use demo mode instead of real OAuth")}</p>
                  </>
                )}
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 px-4 py-2.5 rounded-xl text-xs font-semibold border border-stone-200 text-stone-600 hover:bg-stone-50 transition-colors"
                >
                  {t("Nochmal versuchen", "Try again")}
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 rounded-xl text-xs font-semibold bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors"
                >
                  {t("Schließen", "Close")}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Summary Bar ─────────────────────────────────────────────────────────────

const SummaryBar = ({ total_connected, total_ready, total_warning, total }) => {
  const { t } = useLanguage();
  const tiles = [
    { label: t("Verbunden", "Connected"), value: total_connected, color: "text-stone-800", icon: Wifi },
    { label: t("Postbereit", "Ready to post"), value: total_ready, color: "text-green-600", icon: Zap },
    {
      label: t("Handlungsbedarf", "Needs attention"),
      value: total_warning,
      color: total_warning > 0 ? "text-amber-600" : "text-stone-300",
      icon: AlertTriangle,
    },
    { label: t("Gesamt", "Total"), value: total, color: "text-stone-400", icon: Activity },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {tiles.map(({ label, value, color, icon: Icon }) => (
        <div key={label} className="bg-white rounded-xl border border-stone-200 p-4 text-center">
          <div className="flex items-center justify-center mb-1">
            <Icon size={17} className={color} />
          </div>
          <div className={`text-2xl font-bold ${color}`}>{value}</div>
          <div className="text-xs text-stone-400 mt-0.5 leading-tight">{label}</div>
        </div>
      ))}
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

// Vollständiges Mapping: technischer Fehlercode → nutzersichtbarer Text
// Jeder Eintrag trägt beide Sprachfassungen ({ de, en }) — aufgelöst wird mit t()
// am Aufrufort, da MSG_MAP außerhalb einer Komponente steht und keinen Hook nutzen kann.
const MSG_MAP = {
  access_denied: {
    de: "Du hast die Verbindung abgebrochen. Wenn du fortfahren möchtest, starte sie einfach erneut.",
    en: "You cancelled the connection. If you'd like to continue, just start it again.",
  },
  // LinkedIn schickt NICHT `access_denied`, sondern eigene Codes. Am 14.08. um 15:24
  // kam `user_cancelled_login` zurück — nicht in dieser Tabelle, also las der Owner das
  // nichtssagende „Verbindung fehlgeschlagen. Bitte erneut versuchen." und wusste nicht,
  // dass schlicht das LinkedIn-Fenster geschlossen worden war. Ein Abbruch ist kein
  // Defekt; wer das verwechselt, sucht den Fehler im eigenen System.
  user_cancelled_login: {
    de: "Die Anmeldung bei LinkedIn wurde abgebrochen (Fenster geschlossen oder „Abbrechen“ geklickt). Deine bestehende Verbindung ist unverändert — du kannst es jederzeit erneut versuchen.",
    en: "The LinkedIn login was cancelled (window closed, or \"Cancel\" clicked). Your existing connection is unchanged — you can try again anytime.",
  },
  user_cancelled_authorize: {
    de: "Die Freigabe bei LinkedIn wurde abgelehnt. Ohne die Zustimmung im LinkedIn-Fenster kann kein neuer Zugang erstellt werden — die bestehende Verbindung bleibt davon unberührt.",
    en: "Authorization on LinkedIn was declined. Without your consent in the LinkedIn window, no new access can be created — your existing connection remains unaffected.",
  },
  // 24.08.2026: Der Inhaber hatte „Verbinden“ um 19:29 geklickt, Metas Login-Fenster
  // (Kontowahl, Seiten-/Instagram-Freigaben) dauerte, der Rücksprung kam 20:20 — und
  // wurde verworfen. Der alte Text ließ offen, dass NICHTS gespeichert wurde und der
  // Weg schlicht zu lange offen war. Jetzt sagt er beides.
  state_mismatch: {
    de: "Das Sicherheitsfenster für diese Anmeldung ist abgelaufen (mehr als 45 Minuten zwischen „Verbinden“ und Rücksprung) — es wurde nichts gespeichert, die bestehende Verbindung ist unverändert. Bitte klicke erneut auf „Verbinden“ und schließe den Vorgang in einem Zug ab.",
    en: "The security window for this sign-in expired (more than 45 minutes between “Connect” and the return) — nothing was saved and your existing connection is unchanged. Please click “Connect” again and complete the flow in one go.",
  },
  no_code: {
    de: "Die Anmeldung wurde nicht abgeschlossen — kein Autorisierungscode erhalten.",
    en: "The login wasn't completed — no authorization code was received.",
  },
  token_exchange_failed: {
    de: "Die Verbindung konnte nicht hergestellt werden. Bitte versuche es erneut.",
    en: "The connection couldn't be established. Please try again.",
  },
  oauth_failed: {
    de: "Die Anmeldung ist fehlgeschlagen. Prüfe deine Zugangsdaten und versuche es erneut.",
    en: "The login failed. Please check your credentials and try again.",
  },
  invalid_redirect_uri: {
    de: "LinkedIn-Verbindung fehlgeschlagen: Die Redirect-URL stimmt nicht überein. Bitte die Einstellungen im LinkedIn Developer Portal prüfen.",
    en: "LinkedIn connection failed: the redirect URL doesn't match. Please check the settings in the LinkedIn Developer Portal.",
  },
  invalid_scope: {
    de: "LinkedIn-Verbindung fehlgeschlagen: Fehlende Berechtigungen. Bitte sicherstellen, dass 'Share on LinkedIn' im Developer Portal aktiviert ist.",
    en: "LinkedIn connection failed: missing permissions. Please make sure 'Share on LinkedIn' is enabled in the Developer Portal.",
  },
  app_not_approved: {
    de: "TikTok-Verbindung nicht möglich: Die App ist noch nicht für Video-Posting freigegeben. App-Review im TikTok Developer Portal abschließen.",
    en: "TikTok connection not possible: the app isn't approved for video posting yet. Please complete the app review in the TikTok Developer Portal.",
  },
  scope_not_approved: {
    de: "TikTok-Verbindung fehlgeschlagen: Einige Berechtigungen wurden nicht erteilt. Bitte alle Berechtigungen im TikTok-Anmeldedialog bestätigen.",
    en: "TikTok connection failed: some permissions weren't granted. Please confirm all permissions in the TikTok login dialog.",
  },
  invalid_grant: {
    de: "Der Google-Zugang ist abgelaufen. Bitte verbinde Google Business erneut.",
    en: "Your Google access has expired. Please reconnect Google Business.",
  },
  not_configured: {
    de: "Diese Plattform ist noch nicht eingerichtet. Bitte die Zugangsdaten in den Einstellungen hinterlegen.",
    en: "This platform hasn't been set up yet. Please add the credentials in the settings.",
  },
  channel_locked: {
    de: "Dieser Kanal ist nur für gekaufte Pakete verfügbar.",
    en: "This channel is only available with a purchased plan.",
  },
  // Meta antwortet bei nicht angenommener Tester-Einladung normalerweise direkt dem
  // Browser (rohes JSON, Code 1349245) — kommt der Fehler doch einmal als Redirect
  // zurück, sieht der Kunde hier den Klickpfad statt „Verbindung fehlgeschlagen".
  tester_invite_required: {
    de: "Dieses Konto hat die Tester-Einladung von Meta noch nicht angenommen. Öffne die Threads-App → Profil → Menü → Einstellungen → Konto → Website-Berechtigungen → Einladungen, nimm die Einladung an und versuche es hier erneut.",
    en: "This account hasn't accepted Meta's tester invitation yet. Open the Threads app → Profile → Menu → Settings → Account → Website permissions → Invitations, accept the invitation, and try again here.",
  },
  // ── Bindungs-Zustände (binding_status) aus dem OAuth-Callback ───────────────
  // Das Backend hängt diese Codes als ?message=… an, wenn die Anmeldung zwar
  // geklappt hat, aber KEIN postfähiges Ziel (Seite/Standort) gebunden werden
  // konnte. Ohne diese Einträge verpufften sie als generisches
  // „Verbindung fehlgeschlagen" — der Kunde erfuhr den echten Grund nie.
  keine_standorte: {
    de: "Zu diesem Google-Konto wurde kein Unternehmensprofil gefunden. Lege in Google zuerst ein Unternehmensprofil an (oder lass dich als Inhaber/Verwalter freigeben) und verbinde den Kanal dann erneut.",
    en: "No Business Profile was found for this Google account. First create a Business Profile in Google (or get added as an owner/manager), then reconnect the channel.",
  },
  abruf_fehlgeschlagen: {
    de: "Die Anmeldung hat geklappt, aber die Unternehmensprofile konnten bei Google nicht abgerufen werden. Es kann deshalb noch nicht gepostet werden — bitte später erneut verbinden.",
    en: "The login succeeded, but the business profiles couldn't be retrieved from Google. Posting isn't possible yet — please reconnect later.",
  },
  keine_seiten: {
    de: "Dieses Konto verwaltet keine Facebook-Seite. Ohne Seite kann nicht gepostet werden — lege in Facebook zuerst eine Seite an (oder lass dich als Administrator hinzufügen) und verbinde den Kanal dann erneut.",
    en: "This account doesn't manage a Facebook Page. Posting requires a Page — first create a Page in Facebook (or get added as an admin), then reconnect the channel.",
  },
  auswahl_noetig: {
    de: "Die Anmeldung hat geklappt, aber es wurde noch nichts gebunden: Bitte wähle noch aus, worauf gepostet werden soll. Vorher ist der Kanal nicht postbereit.",
    en: "The login succeeded, but nothing has been linked yet: please choose what to post to. Until then, the channel isn't ready to post.",
  },
  bestaetigung_noetig: {
    de: "Die Anmeldung hat geklappt — bitte bestätige noch das Ziel, auf das gepostet werden soll.",
    en: "The login succeeded — please confirm the destination to post to.",
  },
};

// ─── „Verbunden, aber noch nicht postbereit" ─────────────────────────────────
//
// Der OAuth-Callback leitet auch dann mit ?connected=… zurück, wenn KEIN Ziel
// gebunden wurde (Google Business ohne Standortwahl: &select=1; Facebook mit
// mehreren Seiten: &select=1; genau eine Seite: &confirm=1). Ein „erfolgreich
// verbunden!" wäre dort schlicht falsch — es kann noch nicht gepostet werden.
// Diese Texte sagen ehrlich, was noch fehlt.
// Beide Sprachfassungen je Eintrag — aufgelöst wird mit t() am Aufrufort (diese
// Funktionen stehen außerhalb einer Komponente und bekommen t() als Parameter).
const PENDING_SELECT_MSG = {
  google_business: {
    de: "Google Business: Fast fertig — die Verbindung steht, aber es ist noch kein Unternehmensprofil ausgewählt. Erst danach kann gepostet werden.",
    en: "Google Business: Almost done — the connection is in place, but no Business Profile has been selected yet. Posting is possible only after that.",
  },
  facebook: {
    de: "Facebook: Fast fertig — bitte wähle unten noch die Seite aus, auf die gepostet werden soll.",
    en: "Facebook: Almost done — please select below which Page to post to.",
  },
};
const pendingSelectMsg = (id, t) => {
  const entry = PENDING_SELECT_MSG[id];
  if (entry) return t(entry.de, entry.en);
  return t(
    `${platformLabel(id)}: Fast fertig — bitte wähle noch aus, worauf gepostet werden soll.`,
    `${platformLabel(id)}: Almost done — please select what to post to.`,
  );
};

const PENDING_CONFIRM_MSG = {
  facebook: {
    de: "Facebook: Fast fertig — bitte bestätige unten noch die Seite, auf die gepostet werden soll.",
    en: "Facebook: Almost done — please confirm below the Page to post to.",
  },
};
const pendingConfirmMsg = (id, t) => {
  const entry = PENDING_CONFIRM_MSG[id];
  if (entry) return t(entry.de, entry.en);
  return t(
    `${platformLabel(id)}: Fast fertig — bitte bestätige noch das Ziel, auf das gepostet werden soll.`,
    `${platformLabel(id)}: Almost done — please confirm the destination to post to.`,
  );
};

// binding_status-Werte, die „wirklich nutzbar" bedeuten. Alles andere (inkl.
// auswahl_noetig / bestaetigung_noetig / keine_seiten / keine_standorte) gilt
// als NICHT postbereit. Fehlt das Feld ganz (ältere/andere Endpunkte), wird
// nicht geraten — dann entscheidet allein der Query-Parameter.
const BINDING_READY = ["gebunden", "ready", "ready_to_post"];
const bindingUsable = (bs) =>
  bs === undefined || bs === null || bs === "" || BINDING_READY.includes(String(bs));

// ─── Servergrund sichtbar machen ─────────────────────────────────────────────
//
// FastAPI legt den echten Grund in `detail`. Wird er im catch verschluckt, sieht der
// Kunde einen Fehler OHNE jede Ursache (real passiert: ein 403 „Recovery ist aktuell
// nur für den Hauptaccount verfügbar." kam als nacktes „Prüfung fehlgeschlagen" an).
// Nicht-String-detail (FastAPI-Validierungsliste) wird bewusst NICHT gerendert —
// sonst würde der Toast ein Objekt anzeigen bzw. beim Rendern brechen.
const serverReason = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  const msg = err?.response?.data?.message;
  if (typeof msg === "string" && msg.trim()) return msg.trim();
  return fallback;
};

// ─── Tester-Einladung (Meta-Freigabephase) ───────────────────────────────────
//
// Kanäle, bei denen sich während der Meta-App-Prüfung NUR ein ausdrücklich
// eingeladenes Konto verbinden darf. Ohne angenommene Einladung antwortet Meta dem
// Browser mit rohem JSON (Code 1349245, „The user has not accepted the invite to test
// the app") — der Kunde landet AUSSERHALB unserer App in einer Sackgasse. Deshalb
// steht die Erklärung dauerhaft an der Kanalkarte und nicht nur in einem Toast, der
// nach Sekunden verschwindet.
//
// RAUSNEHMEN, sobald die jeweilige Meta-App die App Review bestanden hat und im
// Live-Modus läuft (Gegenstück: _TESTER_INVITE_REQUIRED in backend/social_oauth/routes.py).
const TESTER_INVITE_CHANNELS = ["threads"];

// Kurzfassung für den Toast — die Langfassung mit Klickpfad steht an der Kanalkarte.
const TESTER_INVITE_TOAST = (label, t) =>
  t(
    `${label} gehört zu Meta und ist noch in der Freigabephase: Solange Meta unsere App prüft, darf sich nur ein ausdrücklich eingeladenes Konto verbinden. Die Schritte dazu stehen direkt unter der ${label}-Karte.`,
    `${label} belongs to Meta and is still in the review phase: while Meta is reviewing our app, only an explicitly invited account can connect. The steps for this are shown directly under the ${label} card.`,
  );

// Der Klickpfad unten beschreibt die Threads-App (heute der einzige Kanal in
// TESTER_INVITE_CHANNELS). Kommt ein weiterer Meta-Kanal dazu, müssen die Schritte
// je Kanal hinterlegt werden statt fest verdrahtet zu bleiben.
const TesterInviteNote = ({ channelId, label, open, onToggle }) => {
  const { t } = useLanguage();
  return (
    <div
      className="rounded-xl border border-violet-200 bg-violet-50 p-3"
      data-testid={`tester-invite-note-${channelId}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex items-start gap-2 w-full text-left"
      >
        <Info size={13} className="text-violet-500 mt-0.5 flex-shrink-0" />
        <span className="flex-1 text-xs font-medium text-violet-800 leading-snug">
          {t(
            `${label} ist noch in der Meta-Freigabephase — so verbindest du dein Konto`,
            `${label} is still in Meta's review phase — here's how to connect your account`,
          )}
        </span>
        <ChevronRight
          size={14}
          className={`text-violet-400 mt-0.5 flex-shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>

      {open && (
        <div className="mt-2.5 space-y-2">
          <p className="text-xs text-violet-700 leading-relaxed">
            {t(
              `${label} gehört zu Meta und ist bei uns noch in der Freigabephase. Solange Meta unsere App prüft, darf sich nur ein ausdrücklich eingeladenes Konto verbinden.`,
              `${label} belongs to Meta and is still in the review phase with us. While Meta is reviewing our app, only an explicitly invited account can connect.`,
            )}
          </p>
          <div className="bg-white border border-violet-100 rounded-lg p-3 space-y-1.5">
            <p className="text-[10px] text-violet-400 uppercase font-semibold tracking-wide">
              {t("So nimmst du die Einladung an", "How to accept the invitation")}
            </p>
            {[
              t("Threads-App öffnen und mit dem Konto deines Unternehmens anmelden.", "Open the Threads app and log in with your business account."),
              t("Profilbild → Menü → Einstellungen → Konto → Website-Berechtigungen → Einladungen.", "Profile picture → Menu → Settings → Account → Website permissions → Invitations."),
              t('Liegt dort eine Einladung von „Förderfuchs“ vor: annehmen.', 'If there\'s an invitation from "Förderfuchs" there: accept it.'),
              t('Danach hier auf „Verbinden“ tippen.', 'Then tap "Connect" here.'),
            ].map((line, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-stone-600 leading-snug">
                <div className="w-4 h-4 rounded-full bg-violet-100 text-violet-500 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">
                  {i + 1}
                </div>
                <span>{line}</span>
              </div>
            ))}
          </div>
          {/* Ehrlich: ob für DIESES Konto schon eine Einladung raus ist, wissen wir hier
              nicht — das entscheidet sich in der Meta-Konsole. Also nichts versprechen. */}
          <p className="text-[11px] text-violet-500 leading-snug">
            {t(
              "Steht dort keine Einladung, sag uns kurz Bescheid — dann müssen wir dein Konto noch einladen, oder wir haben versehentlich ein anderes Konto eingeladen.",
              "If there's no invitation there, let us know — either we still need to invite your account, or we accidentally invited a different one.",
            )}
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Verbundenes Konto ehrlich beschriften ───────────────────────────────────
//
// „Verbunden" allein ist eine stille Lüge, wenn dahinter ein Privatprofil hängt und
// der Kunde seine Firmenseite vermutet. Angezeigt wird ausschließlich, was in den
// Verbindungsdaten wirklich steht — nichts geraten, keine Platzhalter.

// Kontoart nur dort, wo sie aus den Daten ableitbar ist. Bevorzugt das vom Server
// gelieferte Label (/social-connect/status → account_type_label); Rückfall ist die
// gespeicherte LinkedIn-URN (urn:li:person: / urn:li:organization:).
const accountTypeLabel = (channelId, data) => {
  if (channelId !== "linkedin") return null;
  if (typeof data?.account_type_label === "string" && data.account_type_label) {
    return data.account_type_label;
  }
  const urn = String(data?.platform_user_id || "");
  if (urn.startsWith("urn:li:person:")) return "persönliches Profil";
  if (urn.startsWith("urn:li:organization:")) return "Unternehmensseite";
  return null;
};

const ConnectedAccountNote = ({ channelId, data }) => {
  const { t } = useLanguage();
  const name = data?.page_name || data?.profile_name || null;
  if (!name) return null;                       // keine Daten → keine Behauptung
  const typeLabel = accountTypeLabel(channelId, data);
  // Vergleich bewusst gegen die unübersetzte, kanonische Rohfassung — sonst würde
  // ein ins Englische übersetztes Anzeige-Label die Logik sprachabhängig verfälschen.
  const isPersonalLinkedIn =
    channelId === "linkedin" && !!typeLabel && typeLabel !== "Unternehmensseite";
  // Anzeige nur für die beiden bekannten, von uns selbst gesetzten Rohwerte übersetzen —
  // ein vom Server gelieferter account_type_label-Text wird unverändert durchgereicht.
  const typeLabelDisplay =
    typeLabel === "persönliches Profil" ? t("persönliches Profil", "personal profile")
    : typeLabel === "Unternehmensseite" ? t("Unternehmensseite", "company page")
    : typeLabel;

  return (
    <div
      className="rounded-xl border border-stone-200 bg-white px-3 py-2.5"
      data-testid={`connected-as-${channelId}`}
    >
      <p className="text-xs text-stone-600 leading-snug">
        {t("Verbunden als:", "Connected as:")} <span className="font-medium text-stone-800">{name}</span>
        {typeLabel && <span className="text-stone-500"> ({typeLabelDisplay})</span>}
      </p>
      {channelId === "linkedin" && (
        <p className="text-[11px] text-stone-400 mt-1 leading-snug">
          {isPersonalLinkedIn && t(
            "Beiträge erscheinen damit auf diesem persönlichen Profil. ",
            "Posts will therefore appear on this personal profile. ",
          )}
          {t("LinkedIn-Firmenseiten unterstützen wir derzeit noch nicht.", "We don't yet support LinkedIn company pages.")}
        </p>
      )}
    </div>
  );
};

const SocialConnectPage = () => {
  const { t, language } = useLanguage();
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Setup-Assistent + Go-Live-Bereitschaft (config-check)
  const [configCheck, setConfigCheck] = useState(null);         // { channels: { id: {ready,...} } }
  const [setupChannel, setSetupChannel] = useState(null);       // Kanal-ID des offenen Assistenten
  const [rechecking, setRechecking] = useState(false);

  // Wizard-State
  const [wizardPlatform, setWizardPlatform] = useState(null);   // "linkedin" | "tiktok" | null
  const [blueskyOpen, setBlueskyOpen] = useState(false);        // Bluesky-App-Passwort-Modal
  const [wizardSetup, setWizardSetup] = useState(null);         // /setup-status Daten
  const [wizardStep, setWizardStep] = useState(1);              // für Callback-Wiederherstellung
  const [wizardCallbackResult, setWizardCallbackResult] = useState(null);  // "success"|"error"
  const [wizardCallbackMsg, setWizardCallbackMsg] = useState(null);

  // Facebook-Seitenauswahl (Rebind): öffnet sich nach FB-Connect bzw. auf Wunsch,
  // damit die richtige Seite gebunden wird statt stur der ersten.
  const [fbSelectorOpen, setFbSelectorOpen] = useState(false);

  // Welcher Tester-Einladungs-Hinweis (Meta-Freigabephase) ist aufgeklappt?
  // Wird beim Verbinden-Versuch automatisch geöffnet, damit der Klickpfad sichtbar
  // bleibt, nachdem der Toast längst weg ist.
  const [testerNoteOpen, setTesterNoteOpen] = useState(null);

  // Rolle: nur der Inhaber/Master sieht Einrichtungs-Schritte (Portal, Redirect-URI, .env).
  // Für Endkunden ist die Kanal-Freischaltung eine zentrale Inhaber-Aufgabe — kein Dev-Jargon,
  // keine Portal-Sackgasse.
  // FAIL-CLOSED (wie frontend/src/App.js): null = Antwort steht noch aus und zählt als
  // „kein Master". Vorher stand hier `useState(true)` + `is_master !== false` — dadurch
  // blitzten bei langsamer/fehlgeschlagener /account/me-Antwort Betreiber-Werkzeuge beim
  // Kunden auf. Der Inhaber verliert dadurch nur für Sekundenbruchteile seine Zusatz-
  // Ansicht; der Kunde sieht nie etwas, das ihm nicht gehört.
  const [isMaster, setIsMaster] = useState(null);
  useEffect(() => {
    let active = true;
    axios.get(`${API}/account/me`)
      .then((res) => { if (active) setIsMaster(res.data?.is_master === true); })
      .catch(() => { if (active) setIsMaster(false); });
    return () => { active = false; };
  }, []);
  // Betreiber-Elemente werden ausschließlich über `isMaster === true` gerendert —
  // `null` (Antwort steht aus) und `false` verhalten sich damit identisch.

  // Serverautoritativer Plan-/Kanal-Zugriff (GET /billing/plan → channel_access je Kanal).
  // Anzeige-Quelle für Gesperrt/Verbindungsassistent — DURCHGESETZT wird im Backend.
  // Fail-open (null): ohne Plan-Daten Bestandsverhalten; das Backend-Gate greift trotzdem.
  const [planInfo, setPlanInfo] = useState(null);
  useEffect(() => {
    let active = true;
    const load = () => {
      axios.get(`${API}/billing/plan`)
        .then((res) => { if (active) setPlanInfo(res.data || null); })
        .catch(() => { /* Anzeige fail-open — Server-Gate bleibt maßgeblich */ });
    };
    load();
    // Fokus-Refresh: Kauf in anderem Tab (Stripe) → Sperr-Anzeige löst sich ohne Reload.
    window.addEventListener("focus", load);
    return () => { active = false; window.removeEventListener("focus", load); };
  }, []);
  const channelAccess = planInfo?.channel_access || null;
  // Testphase vorbei + kein Kauf → ALLE Verbindungen gesperrt (auch Neu-Verbinden)
  const allLocked = !!planInfo && !planInfo.owner && !planInfo.trial_active && !planInfo.purchased;

  const loadStatus = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await axios.get(`${API}/social-connect/status`);
      setStatusData(res.data);
      // Rückgabe, damit der OAuth-Callback den Bindungszustand prüfen kann,
      // BEVOR er „erfolgreich verbunden" behauptet.
      return res.data;
    } catch (err) {
      // Servergrund zeigen (z. B. 403 mit exakter Ursache) statt nur „ging nicht".
      if (!silent) toast.error(serverReason(err, t("Status konnte nicht geladen werden", "Status could not be loaded")), { duration: 8000 });
      return null;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  // Setup-Status für Wizard laden
  const loadSetupStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/social-oauth/setup-status`);
      setWizardSetup(res.data);
    } catch {
      // non-fatal — Wizard zeigt dann leere Felder
    }
  }, []);

  // Go-Live-Bereitschaft je Kanal laden (für Setup-Assistent). Gibt die frischen Daten zurück.
  const loadConfigCheck = useCallback(async () => {
    const data = await fetchConfigCheck();
    setConfigCheck(data);
    return data;
  }, []);

  // OAuth-Callback und Fehler aus URL-Parametern lesen — einmalig beim ersten Render
  useEffect(() => {
    // Status-Promise merken: der Erfolgs-Toast unten darf erst feuern, wenn der
    // Bindungszustand bekannt ist (kein zweiter Request).
    const statusPromise = loadStatus();
    loadSetupStatus();
    loadConfigCheck();

    const params = new URLSearchParams(window.location.search);
    // Signal des Facebook-Callbacks, dass eine Seiten-AUSWAHL nötig ist (mehrere Seiten
    // → Backend bindet bewusst nichts). Mehrere Alias-Namen werden akzeptiert, damit die
    // Auswahl unabhängig von der exakten Backend-Param-Wahl greift.
    const fbSelectSignal =
      ["select", "select_page", "choose_page", "needs_page_selection"]
        .some((k) => params.get(k) === "facebook") ||
      params.get("facebook_select") === "1" ||
      params.get("page_selection") === "facebook";
    // Generisches Auswahl-Signal des Callbacks: `?connected=<kanal>&select=1`
    // (Google Business ohne Standortwahl, Facebook mit mehreren Seiten).
    // `?…&confirm=1` = gebunden, aber der Nutzer soll das Ziel noch bestätigen.
    const selectFlag = params.get("select") === "1" || params.get("select") === "true";
    const confirmFlag = params.get("confirm") === "1" || params.get("confirm") === "true";
    const hasParams =
      params.has("connected") || params.has("error") || fbSelectSignal ||
      params.has("select") || params.has("select_page") || params.has("choose_page") ||
      params.has("needs_page_selection") || params.has("facebook_select") ||
      params.has("page_selection") || params.has("confirm");

    // Wizard-State aus sessionStorage wiederherstellen (nach OAuth-Redirect)
    let savedWizard = null;
    try {
      const raw = sessionStorage.getItem(WIZARD_SESSION_KEY);
      if (raw) {
        savedWizard = JSON.parse(raw);
        sessionStorage.removeItem(WIZARD_SESSION_KEY);
      }
    } catch {}

    if (params.has("connected")) {
      const connectedId = params.get("connected");
      trackEvent("channel_connected", { platform: connectedId });

      // EHRLICHER TOAST statt Pauschal-Erfolg:
      // `?connected=…` heißt nur „OAuth durch", NICHT „postbereit". Solange kein
      // Ziel gebunden ist (Google-Standort / Facebook-Seite), war der frühere
      // „erfolgreich verbunden!"-Toast schlicht unwahr. Signale in dieser
      // Reihenfolge: Auswahl-Flag → Bestätigungs-Flag → binding_status aus dem
      // Statusabruf (falls der Endpunkt ihn liefert).
      const needsSelection = selectFlag || fbSelectSignal;
      statusPromise
        .then((data) => (data?.platforms || {})[connectedId]?.binding_status)
        .catch(() => undefined)
        .then((bindingStatus) => {
          if (needsSelection || bindingStatus === "auswahl_noetig") {
            toast.info(pendingSelectMsg(connectedId, t), { duration: 9000 });
          } else if (confirmFlag || bindingStatus === "bestaetigung_noetig") {
            toast.info(pendingConfirmMsg(connectedId, t), { duration: 9000 });
          } else if (!bindingUsable(bindingStatus)) {
            // Nur bekannte Codes im Klartext (typeof-Prüfung, damit ein unbekannter
            // Schlüssel nie ein geerbtes Objekt/Funktion in den Toast rendert).
            const known = MSG_MAP[bindingStatus];
            toast.info(
              known && typeof known.de === "string"
                ? t(known.de, known.en)
                : t(
                    `${platformLabel(connectedId)}: Die Anmeldung hat geklappt, aber der Kanal ist noch nicht postbereit.`,
                    `${platformLabel(connectedId)}: The login succeeded, but the channel isn't ready to post yet.`,
                  ),
              { duration: 9000 }
            );
          } else {
            toast.success(t(`${platformLabel(connectedId)} erfolgreich verbunden!`, `${platformLabel(connectedId)} connected successfully!`), { duration: 5000 });
          }
        });

      // War der Wizard aktiv? → Schritt 4 anzeigen. Bei ausstehender Auswahl/
      // Bestätigung KEIN Erfolgs-Häkchen, sondern der ehrliche Zwischenstand.
      if (savedWizard?.platform === connectedId) {
        setWizardPlatform(connectedId);
        setWizardStep(4);
        if (needsSelection) {
          setWizardCallbackResult("error");
          setWizardCallbackMsg(pendingSelectMsg(connectedId, t));
        } else {
          setWizardCallbackResult("success");
        }
      }

      // Facebook: Seite auch bei genau einer Seite explizit anzeigen & bestätigen lassen
      // (Produktregel — keine stille Erstwahl). Bei mehreren Seiten kommt zusätzlich
      // fbSelectSignal aus dem Callback.
      if (connectedId === "facebook") {
        setFbSelectorOpen(true);
      }
    }

    // Facebook-Callback verlangt ausdrücklich eine Seitenauswahl (mehrere Seiten)
    if (fbSelectSignal) {
      trackEvent("facebook_page_selection_prompted", {});
      setFbSelectorOpen(true);
    }

    if (params.has("error")) {
      const errorId = params.get("error");
      const rawMsg = params.get("message") || "";
      // Nur bekannte Codes anzeigen — nie den rohen message-Parameter verbatim
      // (könnte sonst einen durchgereichten Upstream-/Fehlertext leaken).
      const knownErrMsg = MSG_MAP[rawMsg];
      const friendlyMsg = knownErrMsg
        ? t(knownErrMsg.de, knownErrMsg.en)
        : t("Verbindung fehlgeschlagen. Bitte erneut versuchen.", "Connection failed. Please try again.");
      toast.error(`${platformLabel(errorId)}: ${friendlyMsg}`, { duration: 8000 });

      // War der Wizard aktiv? → Schritt 4 / Fehler anzeigen
      if (savedWizard?.platform === errorId) {
        setWizardPlatform(errorId);
        setWizardStep(4);
        setWizardCallbackResult("error");
        setWizardCallbackMsg(friendlyMsg);
      }
    }

    // URL bereinigen — verhindert, dass Params nach Reload erneut angezeigt werden
    if (hasParams) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [loadStatus, loadSetupStatus, loadConfigCheck, t]);

  // ── Wizard öffnen ──
  const handleOpenWizard = useCallback((platform) => {
    if (!isMaster) {
      // Gekaufter Kunde: „Verbindungsassistent" startet den echten Connect-Flow.
      // handleConnect prüft config-check selbst — ist der Kanal zentral noch nicht
      // freigeschaltet, kommt dort die ehrliche Meldung.
      if (channelAccess?.[platform]?.mode === "assistant") {
        handleConnect(platform);
        return;
      }
      // Endkunde ohne Kauf: Kanal-Freischaltung läuft zentral — kein Entwickler-/Portal-
      // Assistent (.env, Developer-Portal, Redirect-URIs), sondern dieselbe ehrliche Meldung.
      toast.info(t(
        `${platformLabel(platform)} schalten wir gerade zentral frei — wir sagen dir Bescheid, sobald er bereit ist.`,
        `We're currently enabling ${platformLabel(platform)} centrally — we'll let you know as soon as it's ready.`,
      ));
      return;
    }
    setWizardPlatform(platform);
    setWizardStep(1);
    setWizardCallbackResult(null);
    setWizardCallbackMsg(null);
    // Setup-Daten refreshen beim Öffnen
    loadSetupStatus();
    // handleConnect ist bewusst kein Dep (wird pro Render neu erzeugt); die von ihm
    // gelesenen States sind über channelAccess/configCheck als Deps abgedeckt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadSetupStatus, isMaster, channelAccess, configCheck, t]);

  // ── Wizard schließen, optional nächste Plattform öffnen ──
  const handleCloseWizard = useCallback((nextPlatform = null) => {
    setWizardPlatform(null);
    setWizardStep(1);
    setWizardCallbackResult(null);
    if (nextPlatform && ["linkedin", "tiktok"].includes(nextPlatform)) {
      setTimeout(() => handleOpenWizard(nextPlatform), 100);
    }
    // Status nach Schließen aktualisieren
    loadStatus(true);
  }, [loadStatus, handleOpenWizard]);

  // ── Upsell (Klick auf gesperrten Kanal) ──
  // Klick wird als Umsatz-Signal geloggt (locked_channel_clicked → analytics_events),
  // dann klarer Upsell-Text + Weg zu den Paketen. Exakter Sperr-Hinweis kommt vom Server.
  const handleUpsell = useCallback((platform) => {
    const access = channelAccess?.[platform];
    const mode = access?.mode || "locked_upsell";
    trackEvent("locked_channel_clicked", { platform, mode });
    toast.info(
      mode === "locked_expired"
        ? t(
            `Deine Testphase ist beendet. ${platformLabel(platform)} und alle weiteren Kanäle schaltest du mit einem gekauften Paket frei.`,
            `Your trial period has ended. Unlock ${platformLabel(platform)} and all other channels with a purchased plan.`,
          )
        : t(
            `${platformLabel(platform)} ist nur für gekaufte Pakete verfügbar. Mit deinem Paket schaltest du alle Kanäle frei.`,
            `${platformLabel(platform)} is only available with a purchased plan. Your plan unlocks all channels.`,
          ),
      {
        duration: 8000,
        action: { label: t("Pakete ansehen", "View plans"), onClick: () => { window.location.href = "/pricing"; } },
      }
    );
  }, [channelAccess, t]);

  // ── Connect ──
  const handleConnect = async (platform, { fromAssistant = false } = {}) => {
    // Sicherheitsnetz: gesperrte Kanäle nie anstoßen (Server würde ohnehin 403en) —
    // stattdessen derselbe Upsell-Pfad wie beim Button-Klick.
    const chAccess = channelAccess?.[platform];
    if (chAccess && chAccess.allowed === false) {
      handleUpsell(platform);
      return;
    }
    // Noch nicht bereit (config-check) → geführter Setup-Assistent statt Fehlermeldung.
    // Bluesky ist immer bereit (App-Passwort) → normaler Pfad.
    if (!fromAssistant && platform !== "bluesky") {
      const info = configCheck?.channels?.[platform];
      // CONN-02 (16.08.2026): `loadConfigCheck()` läuft unabhängig vom Vollbild-Spinner
      // (der hängt nur an `loadStatus`). Klickte ein Kunde, bevor die Antwort da war,
      // war `info` noch undefined — die Bedingung darunter griff nicht, und der Klick
      // lief ungebremst durch. Ein noch unbekannter Zustand ist kein „erlaubt":
      // lieber einen Moment warten lassen als in eine Anbieter-Fehlerseite schicken.
      // (Der Backend-Riegel aus CONN-01 fängt es seit derselben Runde ohnehin ab —
      // dies hier erspart dem Kunden nur den Umweg.)
      if (info === undefined && configCheck === null) {
        toast.info(t(
          "Einen Moment noch — wir prüfen gerade, welche Kanäle für dich bereit sind.",
          "Just a moment — we're currently checking which channels are ready for you.",
        ));
        return;
      }
      if (info && info.ready === false && !info.backend_pending) {
        if (!isMaster) {
          if (TESTER_INVITE_CHANNELS.includes(platform)) {
            // Meta-Freigabephase: „schalten wir zentral frei" wäre hier zwar nicht
            // falsch, aber nutzlos — der Kunde MUSS selbst eine Tester-Einladung
            // annehmen. Deshalb: Hinweis dauerhaft aufklappen und den echten
            // Servergrund holen, ohne jemals zu Meta zu navigieren (dort käme nur
            // rohes JSON, außerhalb unserer App).
            setTesterNoteOpen(platform);
            const probe = await startConnect(platform);
            const backendMsg =
              probe.action === "setup_needed" || probe.action === "error" ? probe.message : null;
            toast.info(backendMsg || TESTER_INVITE_TOAST(platformLabel(platform), t), {
              duration: 12000,
            });
            return;
          }
          // Endkunde: Kanal ist zentral noch nicht freigeschaltet — keine Portal-/Admin-Sackgasse,
          // sondern eine ehrliche, handlungslose Meldung.
          toast.info(t(
            `${platformLabel(platform)} schalten wir gerade zentral frei — wir sagen dir Bescheid, sobald er bereit ist.`,
            `We're currently enabling ${platformLabel(platform)} centrally — we'll let you know as soon as it's ready.`,
          ));
          return;
        }
        setSetupChannel(platform);
        return;
      }
    }
    setConnecting(platform);
    // Zentrale API-Schicht: gibt eine klare Aktion zurück (redirect / credentials / setup_needed / error)
    const result = await startConnect(platform);
    if (result.action === "redirect") {
      // Sicherheitsnetz für Kanäle in der Meta-Freigabephase: ohne angenommene
      // Tester-Einladung antwortet Meta dem Browser mit rohem JSON — der Kunde säße
      // dann AUSSERHALB unserer App fest. Der Betreiber (Master) darf weiter, er
      // verwaltet die Einladungen selbst.
      if (!isMaster && TESTER_INVITE_CHANNELS.includes(platform)) {
        setTesterNoteOpen(platform);
        toast.info(TESTER_INVITE_TOAST(platformLabel(platform), t), { duration: 12000 });
        setConnecting(null);
        return;
      }
      window.location.href = result.url;
      return; // Navigation läuft — connecting-State bleibt bis zum Verlassen der Seite
    }
    if (result.action === "credentials") {
      // Bluesky: App-Passwort-Modal statt OAuth-Redirect
      setBlueskyOpen(true);
    } else if (result.action === "setup_needed") {
      // Backend-Wortlaut UNVERÄNDERT durchreichen (kein generischer Ersatztext) und
      // lang genug stehen lassen, dass man ihn lesen kann.
      if (TESTER_INVITE_CHANNELS.includes(platform)) setTesterNoteOpen(platform);
      toast.info(result.message, { duration: 12000 }); // ehrlicher Hinweis, keine vorgetäuschte Verbindung
    } else {
      toast.error(result.message);
    }
    setConnecting(null);
  };

  // ── Disconnect ──
  const handleDisconnect = async (platform) => {
    const name = platformLabel(platform);
    if (!window.confirm(t(`${name} wirklich trennen?`, `Really disconnect ${name}?`))) return;
    try {
      await axios.delete(`${API}/social-connect/${platform}`);
      toast.success(t(`${name} wurde getrennt`, `${name} has been disconnected`));
      await loadStatus(true);
    } catch (err) {
      // Servergrund zeigen — sonst bleibt unklar, WARUM das Trennen scheiterte.
      toast.error(serverReason(err, t("Trennen fehlgeschlagen", "Disconnecting failed")), { duration: 8000 });
    }
  };

  // ── Recovery / Health-Check ──
  const handleRecover = async (platform) => {
    setConnecting(platform);
    try {
      const res = await axios.post(`${API}/social-connect/recover/${platform}`);
      const action = res.data?.recovery_action;
      const name = platformLabel(platform);

      switch (action?.action) {
        case "refresh_token":
          toast.success(t(`${name}: Verbindung erfolgreich erneuert`, `${name}: connection successfully renewed`));
          await loadStatus(true);
          break;
        case "reconnect":
          toast.info(t(`${name}: Bitte neu anmelden`, `${name}: please log in again`), { duration: 4000 });
          setConnecting(null);
          await handleConnect(platform);
          return;
        case "grant_permissions":
          toast.info(t(`${name}: Fehlende Berechtigungen — bitte neu verbinden`, `${name}: missing permissions — please reconnect`), {
            duration: 5000,
          });
          setConnecting(null);
          await handleConnect(platform);
          return;
        case "wait":
          toast.info(action.description || t(`${name}: Bitte kurz warten`, `${name}: please wait a moment`), {
            duration: 6000,
          });
          await loadStatus(true);
          break;
        default: {
          // Der Text des Backends wird NICHT ungeprüft durchgereicht: Für einen gesunden
          // Kanal liefert get_recovery_action() „Unbekanntes Problem — bitte Support
          // kontaktieren", und das stand am 14.08. als grüne Erfolgsmeldung über einem
          // Kanal, dem nichts fehlte. Siehe components/social/recoverMessage.js.
          const meldung = recoverMessage(name, action, res.data?.previous_status);
          (meldung.tone === "info" ? toast.info : toast.success)(meldung.text, { duration: 6000 });
          await loadStatus(true);
        }
      }
    } catch (err) {
      // Der eigentliche Beschwerdefall: hier stand ein nacktes `catch {}`, das den
      // Servergrund verschluckt hat („Firmen-Verbindung schlägt fehl" ohne Grund).
      toast.error(
        serverReason(err, t("Prüfung fehlgeschlagen — bitte Verbindung kontrollieren", "Check failed — please review the connection")),
        { duration: 8000 },
      );
    } finally {
      setConnecting(null);
    }
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-fox-fire" />
      </div>
    );
  }

  const platforms_data = statusData?.platforms || {};

  // Kennzahlen für SummaryBar berechnen
  const total_connected = statusData?.total_connected ?? 0;
  const total_ready = statusData?.total_ready ?? 0;
  const total_warning = PLATFORMS.filter((p) =>
    WARN_STATUSES.includes(platforms_data[p.id]?.status)
  ).length;

  // Gibt es Plattformen mit Handlungsbedarf?
  const hasWarnings = total_warning > 0;

  // Ist Facebook verbunden? → dann darf der Inhaber die aktive Seite jederzeit wechseln.
  const fbData = platforms_data.facebook || {};
  const facebookConnected =
    fbData.connected === true ||
    [S.CONNECTED, S.READY].includes(fbData.status);

  // Kanal-Reihenfolge: verbunden > verbindbar (connect/assistant) > gesperrt.
  // Ohne Plan-Daten (planInfo null) bleibt die Original-Reihenfolge erhalten.
  const _connState = (p) => {
    const st = platforms_data[p.id]?.status;
    if (st === S.CONNECTED || st === S.READY) return 0;         // verbunden
    const acc = channelAccess?.[p.id];
    if (!acc || acc.allowed !== false) return 1;                // verbindbar
    return 2;                                                   // gesperrt
  };
  const orderedPlatforms = channelAccess
    ? [...PLATFORMS].sort((a, b) => _connState(a) - _connState(b))
    : PLATFORMS;

  return (
    <div className="space-y-8 animate-fade-in">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-stone-900">{t("Kanäle verbinden", "Connect channels")}</h1>
          <p className="text-stone-500 mt-1">
            {t("Verbinde deine Social Media Konten für automatisches Posten", "Connect your social media accounts for automatic posting")}
          </p>
        </div>
        <button
          onClick={() => loadStatus(true)}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 rounded-xl border border-stone-200 text-stone-600 hover:bg-stone-50 text-sm font-medium transition-colors"
        >
          <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
          {t("Aktualisieren", "Refresh")}
        </button>
      </div>

      {/* ── Summary Bar ── */}
      <SummaryBar
        total_connected={total_connected}
        total_ready={total_ready}
        total_warning={total_warning}
        total={PLATFORMS.length}
      />

      {/* ── Geh live: sobald ein Kanal bereit ist → ersten Beitrag veröffentlichen ── */}
      {total_ready >= 1 && (
        <div
          data-testid="go-live-cta"
          className="rounded-xl border border-green-200 bg-green-50 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle size={18} className="text-green-600" />
            </div>
            <div>
              <p className="font-semibold text-stone-800">{t("Startklar — du kannst live gehen!", "Ready to go live!")}</p>
              <p className="text-sm text-stone-600">
                {t(
                  "Mindestens ein Kanal ist verbunden. Gib jetzt deinen ersten Beitrag aus dem Monatsplan frei und veröffentliche ihn.",
                  "At least one channel is connected. Approve your first post from the monthly plan now and publish it.",
                )}
              </p>
            </div>
          </div>
          <NavLink
            to="/monthly-plan"
            className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-fox-fire text-white font-medium hover:bg-orange-600 whitespace-nowrap flex-shrink-0"
          >
            {t("Beitrag freigeben & veröffentlichen", "Approve & publish post")} <ArrowRight size={16} />
          </NavLink>
        </div>
      )}

      {/* ── Einrichtungs-Helfer: exakte Weiterleitungs-Adressen je Portal (einmalig) ──
           Reine Inhaber-/Admin-Aufgabe (Portal, Redirect-URI, Env). Für Endkunden ausgeblendet. */}
      {isMaster === true && <RedirectUriHelp />}

      {/* ── Banner: Konfiguration fehlt (nur wenn mind. eine Plattform betroffen) ── */}
      {IS_LOCAL_DEV && Object.values(platforms_data).some(p => p?.recommended_action === "configure") && (
        <div className="flex items-start gap-3 p-4 bg-sky-50 border border-sky-200 rounded-xl">
          <Info size={18} className="text-sky-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-sky-800 text-sm">
              {t("Einige Plattformen noch nicht konfiguriert", "Some platforms not yet configured")}
            </p>
            <p className="text-sm text-sky-600 mt-0.5">
              {t("CLIENT_ID / CLIENT_SECRET in", "Add CLIENT_ID / CLIENT_SECRET to")}{" "}
              <code className="bg-sky-100 px-1 rounded text-xs">backend/.env</code>{" "}
              {t(
                "ergänzen und Backend neu starten. Plattformen mit vollständiger Konfiguration sind bereits verbindbar.",
                "and restart the backend. Platforms with a complete configuration are already connectable.",
              )}
            </p>
          </div>
        </div>
      )}

      {/* ── Banner: Handlungsbedarf ── (nicht bei Komplett-Sperre: Neu-Verbinden ist
           dann ebenfalls gesperrt — das Testphase-beendet-Banner erklärt den Zustand) */}
      {hasWarnings && !allLocked && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <AlertTriangle size={18} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-amber-800 text-sm">
              {total_warning === 1
                ? t("Eine Plattform benötigt deine Aufmerksamkeit", "One platform needs your attention")
                : t(`${total_warning} Plattformen benötigen deine Aufmerksamkeit`, `${total_warning} platforms need your attention`)}
            </p>
            <p className="text-sm text-amber-600 mt-0.5">
              {t(
                "Bitte die markierten Kanäle neu verbinden, um automatisches Posten sicherzustellen.",
                "Please reconnect the flagged channels to ensure automatic posting.",
              )}
            </p>
          </div>
        </div>
      )}

      {/* ── Banner: Testphase (nur Bluesky) / Testphase beendet ── */}
      {planInfo && !planInfo.owner && planInfo.trial_active && !planInfo.purchased && (
        <div data-testid="trial-channel-banner" className="flex items-start gap-3 p-4 bg-sky-50 border border-sky-200 rounded-xl">
          <Info size={18} className="text-sky-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-sky-800 text-sm">
              {t("Testphase: Verbinde Bluesky und poste sofort los", "Trial period: connect Bluesky and start posting right away")}
            </p>
            <p className="text-sm text-sky-600 mt-0.5">
              {t(
                "In der 14-Tage-Testphase nutzt du alle Funktionen und veröffentlichst über Bluesky. Alle weiteren Kanäle sind nur für gekaufte Pakete — nach dem Kauf verbindest du sie mit dem Verbindungsassistenten.",
                "During the 14-day trial you use all features and publish via Bluesky. All other channels are only available with a purchased plan — after purchase, you connect them with the connection wizard.",
              )}
            </p>
          </div>
          <NavLink
            to="/pricing"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 whitespace-nowrap flex-shrink-0"
          >
            {t("Pakete ansehen", "View plans")} <ArrowRight size={14} />
          </NavLink>
        </div>
      )}
      {planInfo && !planInfo.owner && !planInfo.trial_active && !planInfo.purchased && (
        <div data-testid="trial-expired-banner" className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <AlertTriangle size={18} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-amber-800 text-sm">{t("Deine Testphase ist beendet", "Your trial period has ended")}</p>
            <p className="text-sm text-amber-700 mt-0.5">
              {t(
                "Deine verbundenen Kanäle und alle geplanten Beiträge bleiben gespeichert — veröffentlicht wird erst wieder mit deinem Paket. Neue Kanal-Verbindungen sind bis zum Kauf gesperrt; danach verbindest du alle Kanäle mit dem Verbindungsassistenten.",
                "Your connected channels and all scheduled posts stay saved — publishing resumes only with a purchased plan. New channel connections are locked until purchase; afterwards you connect all channels with the connection wizard.",
              )}
            </p>
          </div>
          <NavLink
            to="/pricing"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-fox-fire text-white text-sm font-medium hover:bg-orange-600 whitespace-nowrap flex-shrink-0"
          >
            {t("Paket wählen", "Choose a plan")} <ArrowRight size={14} />
          </NavLink>
        </div>
      )}

      {/* ── Banner: Noch nichts verbunden ── */}
      {total_connected === 0 && !hasWarnings && (
        <div className="flex items-start gap-3 p-4 bg-stone-50 border border-stone-200 rounded-xl">
          <WifiOff size={18} className="text-stone-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-stone-700 text-sm">{t("Noch keine Plattform verbunden", "No platform connected yet")}</p>
            <p className="text-sm text-stone-500 mt-0.5">
              {t(
                "Verbinde mindestens eine Plattform, um automatisches Posten zu aktivieren.",
                "Connect at least one platform to enable automatic posting.",
              )}
            </p>
          </div>
        </div>
      )}

      {/* ── Connect Wizard Panel (LinkedIn / TikTok) ── */}
      {wizardPlatform && (
        <ConnectWizardPanel
          platformId={wizardPlatform}
          setupData={wizardSetup}
          onStartConnect={handleConnect}
          onClose={handleCloseWizard}
          initialStep={wizardStep}
          callbackResult={wizardCallbackResult}
          callbackMessage={wizardCallbackMsg}
        />
      )}

      {/* ── Facebook-Seitenauswahl (Rebind) ──
           Öffnet sich nach FB-Connect (Callback) oder per „Seite wechseln"-Button.
           Bindet die bewusst GEWÄHLTE Seite statt stur der ersten. */}
      <FacebookPageSelector
        open={fbSelectorOpen}
        onClose={() => setFbSelectorOpen(false)}
        onBound={() => loadStatus(true)}
        onReconnect={() => { setFbSelectorOpen(false); handleConnect("facebook", { fromAssistant: true }); }}
      />

      {/* Manueller Zugang: aktive Facebook-Seite jederzeit prüfen/wechseln */}
      {facebookConnected && !fbSelectorOpen && (
        <div className="flex items-center justify-between gap-3 p-3 rounded-xl border border-blue-200 bg-blue-50">
          <div className="flex items-center gap-2 min-w-0">
            <Facebook size={16} className="text-blue-600 flex-shrink-0" />
            <p className="text-sm text-stone-600 truncate">
              {t("Facebook postet auf", "Facebook posts to")}{" "}
              <span className="font-medium text-stone-800">
                {fbData.page_name || t("der aktuell gebundenen Seite", "the currently linked page")}
              </span>
              . {t("Falsche Seite? Du kannst sie hier wechseln.", "Wrong page? You can change it here.")}
            </p>
          </div>
          <button
            onClick={() => setFbSelectorOpen(true)}
            data-testid="facebook-change-page"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-blue-300 text-blue-700 hover:bg-blue-100 transition-colors whitespace-nowrap flex-shrink-0"
          >
            <RefreshCw size={13} /> {t("Seite wechseln", "Change page")}
          </button>
        </div>
      )}

      <BlueskyConnectModal
        open={blueskyOpen}
        onClose={() => setBlueskyOpen(false)}
        onConnected={(res) => {
          setBlueskyOpen(false);
          toast.success(res?.message || t("Bluesky verbunden", "Bluesky connected"));
          loadStatus(true);
        }}
      />

      <ChannelSetupAssistant
        key={setupChannel || "none"}
        open={!!setupChannel}
        channelId={setupChannel}
        channelLabel={platformLabel(setupChannel)}
        info={configCheck?.channels?.[setupChannel]}
        setupHelpEnabled={configCheck?.setup_help_enabled === true}
        rechecking={rechecking}
        onRecheck={async () => {
          setRechecking(true);
          await loadConfigCheck();
          setRechecking(false);
        }}
        onConnectNow={() => {
          const id = setupChannel;
          setSetupChannel(null);
          handleConnect(id, { fromAssistant: true });
        }}
        onClose={() => setSetupChannel(null)}
      />

      {/* ── Platform Cards ── */}
      {/* Reihenfolge: verbundene und aktuell verbindbare Kanäle zuerst, gesperrte danach.
          In der Testphase steht so Bluesky (der einzige verbindbare Kanal) ganz oben,
          statt hinter 7 gesperrten Karten zu verschwinden. */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {orderedPlatforms.map((platform) => {
          const pData = platforms_data[platform.id];
          const pConnected = [S.CONNECTED, S.READY].includes(pData?.status);
          // SocialChannelCard rendert channel.description direkt als JSX-Text und liegt
          // außerhalb dieser Datei — die englische Fassung wird deshalb hier ausgewählt,
          // bevor die Kanaldaten als Prop übergeben werden.
          const displayPlatform = language === "en"
            ? { ...platform, description: platform.descriptionEn || platform.description }
            : platform;
          return (
            <div key={platform.id} className="flex flex-col gap-2">
              <SocialChannelCard
                channel={displayPlatform}
                accountData={pData}
                onConnect={handleConnect}
                onDisconnect={handleDisconnect}
                onRecover={handleRecover}
                connecting={connecting}
                onOpenWizard={handleOpenWizard}
                onUpsell={handleUpsell}
                access={channelAccess?.[platform.id]}
                pendingReview={
                  // Gekaufter Kunde + technisch konfiguriert, aber vom Betreiber noch nicht
                  // freigeschaltet (App-Review) → ehrliches „In Freischaltung" statt Sackgasse.
                  channelAccess?.[platform.id]?.mode === "assistant" &&
                  configCheck?.channels?.[platform.id]?.pending_review === true
                }
              />
              {/* Verbunden: WOMIT genau? Facebook hat dafür schon einen eigenen
                  Seiten-Hinweis oben — hier nicht doppeln. */}
              {pConnected && platform.id !== "facebook" && (
                <ConnectedAccountNote channelId={platform.id} data={pData} />
              )}
              {/* Noch nicht verbunden + Meta-Freigabephase → dauerhafter Klickpfad.
                  Bei gesperrtem Kanal (kein Paket) wäre der Klickpfad nutzlos —
                  dort bleibt der Upsell-Hinweis der Karte allein stehen. */}
              {!pConnected && TESTER_INVITE_CHANNELS.includes(platform.id) &&
                channelAccess?.[platform.id]?.allowed !== false && (
                <TesterInviteNote
                  channelId={platform.id}
                  label={platform.label}
                  open={testerNoteOpen === platform.id}
                  onToggle={() =>
                    setTesterNoteOpen(testerNoteOpen === platform.id ? null : platform.id)
                  }
                />
              )}
            </div>
          );
        })}
      </div>

      {/* ── Info-Box ── */}
      <div className="bg-stone-50 border border-stone-200 rounded-xl p-5">
        <h3 className="font-semibold text-stone-700 text-sm mb-3 flex items-center gap-2">
          <CheckCircle size={15} className="text-green-500" />
          {t("So funktioniert die Verbindung", "How the connection works")}
        </h3>
        <ul className="space-y-2 text-sm text-stone-500">
          <li className="flex items-start gap-2">
            <span className="text-fox-fire mt-0.5 font-medium">1.</span>
            {t(
              'Klicke bei deinem Kanal auf „Verbinden" bzw. „Verbindungsassistent" — du wirst zur offiziellen Anmeldeseite weitergeleitet.',
              'Click "Connect" or "Connection wizard" on your channel — you\'ll be redirected to the official login page.',
            )}
          </li>
          <li className="flex items-start gap-2">
            <span className="text-fox-fire mt-0.5 font-medium">2.</span>
            {t(
              "Melde dich an und erteile die Berechtigungen für Posts und Seitenverwaltung.",
              "Log in and grant the permissions for posting and page management.",
            )}
          </li>
          <li className="flex items-start gap-2">
            <span className="text-fox-fire mt-0.5 font-medium">3.</span>
            {t(
              "Du wirst zurück zu Förderfuchs geleitet — der Kanal ist sofort bereit.",
              "You'll be redirected back to Förderfuchs — the channel is ready immediately.",
            )}
          </li>
          <li className="flex items-start gap-2">
            <span className="text-fox-fire mt-0.5 font-medium">4.</span>
            {t(
              "Zugangsdaten werden verschlüsselt gespeichert und bei Bedarf automatisch erneuert.",
              "Credentials are stored encrypted and refreshed automatically when needed.",
            )}
          </li>
        </ul>
      </div>
    </div>
  );
};

export default SocialConnectPage;
